import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import noop from 'lodash/noop';
import React, { useContext, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { createItem, createItemBinding } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, ItemCreatePayload, Translation } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { APIErrorNotification, WoltModal } from 'common/components';
import { Typography as T } from 'common/typography';
import { getErrorContent, localize } from 'common/utils';
import { MenuLocaleContext } from 'modules/menu/MenuLocaleContext';
import JsonFileDropzone from 'modules/menu/components/JsonFileDropzone';
import { useImportItemsTracking } from 'modules/menu/hooks/telemetryHooks';
import useMenuData from 'modules/menu/useMenuData';

import { reportCategoryItemsImport } from '../../behavioral';
import { getItemNamesWithUnknownOptions, readAndParseImportFile, serializeImportItems } from '../utils';

type Props = {
  className?: string;
  category: Category;
  isOpen: boolean;
  menuId: string;
  onClose: () => void;
};

const Root = styled(WoltModal)`
  width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: ${cbSemanticCssVars.surfaceMain};
  color: ${cbSemanticCssVars.textPrimary};
  border-radius: 8px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Description = styled(T.Body3)`
  padding: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const FileContainer = styled.div`
  padding: 24px;
`;

const Container = styled(T.Body3)`
  display: flex;
  flex-direction: column;
  padding-block-start: 24px;
`;

const SuccessExplanation = styled.div`
  padding: 0 24px 24px;
`;

const AlertContainer = styled.div`
  padding: 0 24px 24px;
`;

const ErrorListContainer = styled(T.Body3)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-height: 200px;
  overflow-y: auto;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  justify-content: flex-end;
`;

const ImportItemsModal = ({ className, category, isOpen, menuId, onClose }: Props) => {
  const { options, updateMenuData } = useMenuData();

  const { primaryLanguage } = useContext(MenuLocaleContext);

  const intl = useIntl();

  const [file, setFile] = useState<File | null>(null);
  const [hasFileError, setHasFileError] = useState(false);
  const [failedItems, setFailedItems] = useState<{ itemName: Translation[]; error: string | undefined }[]>([]);

  const trackImportItems = useImportItemsTracking(category.id);

  const { mutateAsync: createAndUpdateItem } = useMutation(
    async (payload: ItemCreatePayload) => {
      const createdItem = await createItem(menuId, payload);
      updateMenuData({ actionType: 'addItem', payload: createdItem });
      const updatedMenu = await createItemBinding(payload.menu_id, category.id, { item_id: createdItem.id });
      updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
    },
    {
      onError: (error, payload) => {
        setFailedItems(prev => [
          ...prev,
          {
            itemName: payload.name,
            error: getErrorContent(error),
          },
        ]);
      },
    },
  );

  const {
    mutateAsync: handleFileChange,
    isLoading,
    error: importError,
    isSuccess: isImportDone,
    reset: resetImport,
    data,
  } = useMutation(
    async () => {
      if (file === null) {
        return;
      }
      setFailedItems([]);
      const importedData = await readAndParseImportFile(file, intl);
      const itemCreatePayloads = serializeImportItems(menuId, options, importedData);

      for (const payload of itemCreatePayloads) {
        await createAndUpdateItem(payload).catch(noop);
      }
      return { itemCreatePayloads, importedData, failedItems };
    },
    {
      onSuccess: data => {
        if (!data) {
          return;
        }
        trackImportItems(data.failedItems);
        reportCategoryItemsImport();
      },
    },
  );

  const itemNamesWithUnknownOptions = useMemo(
    () => (data ? getItemNamesWithUnknownOptions(options, data.importedData) : []),
    [data, options],
  );

  const allItemsFailed = useMemo(
    () => failedItems.length === data?.itemCreatePayloads.length,
    [data?.itemCreatePayloads.length, failedItems.length],
  );

  const handleClose = () => {
    if (isImportDone) {
      resetImport();
    }
    setFile(null);
    onClose();
  };

  return (
    <Root className={className} isOpen={isOpen} onRequestClose={handleClose}>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="menu.importItemsHeader" />
      </ModalHeader>
      <Content>
        {!isImportDone && (
          <>
            <Description>
              <FormattedMessage id="menu.importItemsExplanation" />
            </Description>
            <FileContainer>
              <JsonFileDropzone
                fileName={file?.name}
                onDrop={files => {
                  setHasFileError(false);
                  if (!files[0].name.endsWith('.json')) {
                    setHasFileError(true);
                  }
                  setFile(files[0]);
                }}
                loading={isLoading}
                hasError={hasFileError}
              />
              {hasFileError && (
                <InputAlert style={{ marginBlockStart: 8 }} variant="error">
                  <FormattedMessage id="menu.fileMustBeJson" />
                </InputAlert>
              )}
            </FileContainer>
          </>
        )}
        {isImportDone && (
          <Container>
            {!allItemsFailed && (
              <SuccessExplanation>
                <FormattedMessage id="menu.importItemsSuccess" /> 👍
              </SuccessExplanation>
            )}
            {failedItems.length > 0 && (
              <AlertContainer>
                <Alert title={intl.formatMessage({ id: 'menu.importItemsFailedItems' })} variant="error">
                  <ErrorListContainer>
                    {failedItems.map(itemNameWithError => (
                      <div>
                        <T.Small>{localize(itemNameWithError.itemName, primaryLanguage)}</T.Small>
                        {itemNameWithError.error && <T.Small>: {itemNameWithError.error}</T.Small>}
                      </div>
                    ))}
                  </ErrorListContainer>
                </Alert>
              </AlertContainer>
            )}
            {!allItemsFailed && itemNamesWithUnknownOptions.length > 0 && (
              <AlertContainer>
                <Alert title={intl.formatMessage({ id: 'menu.importItemsIncompatibleItems' })} variant="warning">
                  <ErrorListContainer>
                    {itemNamesWithUnknownOptions.map(itemName => (
                      <T.Small>{localize(itemName, primaryLanguage)}</T.Small>
                    ))}
                  </ErrorListContainer>
                </Alert>
              </AlertContainer>
            )}
          </Container>
        )}
      </Content>
      <StyledModalFooter>
        {!!importError && <APIErrorNotification error={importError} style={{ marginInlineEnd: 16, flex: '1' }} />}
        {isImportDone ? (
          <Button variant="secondary" size="small" onClick={handleClose}>
            <FormattedMessage id="state.close" />
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="small" onClick={handleClose} style={{ marginInlineEnd: '16px' }}>
              <FormattedMessage id="state.cancel" />
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={() => handleFileChange()}
              disabled={!file}
              loading={isLoading}
            >
              <FormattedMessage id="state.submit" />
            </Button>
          </>
        )}
      </StyledModalFooter>
    </Root>
  );
};

export default ImportItemsModal;
